import {
  AdjustmentsHorizontalIcon,
  Cog8ToothIcon,
  GlobeAltIcon,
  PlusCircleIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import {
  CloudPolicy,
  CloudProvider,
  CreateCloudPolicyRequest,
  DefaultRule,
  getDefaultRuleString,
  getDefaultRuleEnum,
} from "components/CloudPolicy/types";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { FilterIcon, Globe } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import PolicyCard from "./PolicyCard";
import CreatePolicy, { DEFAULT_RULE_OPTIONS } from "./CreatePolicy";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { FlatCard, PulsingIndicator, Spinner } from "components/UI-lib";
import EditPolicy from "./EditPolicy";
import agent from "server";
import { transformWorkspaceToEnvironment } from "components/Environment/utils/environtmentUtils";
import { useQuery } from "@tanstack/react-query";
import { NotificationContext } from "contexts/NotificationContext";

export const CloudPolicyComponent: React.FC<{
  orgId: string;
}> = ({ orgId }) => {
  const notificationContext = useContext(NotificationContext);
  const [policies, setPolicies] = useState<CloudPolicy[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState<CloudPolicy | null>(
    null
  );
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [filterProvider, setFilterProvider] = useState<string>("all");

  const [organizationWideDefaultRule, setOrganizationWideDefaultRule] =
    useState<DefaultRule | null>(null);
  const [isOrgDefaultRulePopoverOpen, setIsOrgDefaultRulePopoverOpen] =
    useState(false);
  const [tempOrgDefaultRule, setTempOrgDefaultRule] =
    useState<DefaultRule | null>(null);
  const [errorCreatePolicy, setErrorCreatePolicy] = useState<string | null>(
    null
  );
  const [isDeletingPolicy, setIsDeletingPolicy] = useState(false);
  const [isCreatingOrgWideDefaultRule, setIsCreatingOrgWideDefaultRule] =
    useState(false);
  const [errorMessageOrgWideDefaultRule, setErrorMessageOrgWideDefaultRule] =
    useState<string | null>(null);

  const getOrgWorkspaces = async (orgId: string) => {
    const res = await agent.Organizations.getWorkspaceGroups(orgId);
    if (res.success && res.data) {
      return res.data
        .filter(
          (workspace) =>
            workspace.status === "RUNNING" && workspace.id !== "akash-brev-wg"
        )
        .sort((a, b) => a.platformType.localeCompare(b.platformType))
        .map((workspace) => ({
          id: workspace.platformId,
          name:
            workspace.platformType.toUpperCase() +
            " (" +
            workspace.platformId +
            ")",
        }));
    } else {
      throw new Error(res.message);
    }
  };

  const {
    data: cloudProviders = [],
    isLoading: isCloudProvidersLoading,
    error: userError,
  } = useQuery({
    queryKey: ["admin-org-workspaces", orgId],
    queryFn: () => getOrgWorkspaces(orgId),
    enabled: !!orgId,
    staleTime: 2000,
  });

  const {
    data: policiesData = [],
    isLoading: isPoliciesLoading,
    error: policiesError,
  } = useQuery({
    queryKey: ["admin-org-policies", orgId],
    queryFn: () => getOrgPolicies(orgId),
    enabled: !!orgId,
    staleTime: 2000,
  });

  const getOrgPolicies = async (orgId: string) => {
    const res = await agent.Admin.listCloudPolicies(orgId, {
      options: {
        organization_id: orgId,
        cloud_cred_id: "*",
      },
    });
    if (res.success && res.data) {
      return res.data.map((policy) => ({
        ...policy,
      }));
    } else {
      throw new Error(res.message);
    }
  };

  const filteredPolicies = useMemo(() => {
    return policies
      .filter((policy) => !!policy.cloud_cred_id)
      .filter(
        (policy) =>
          policy.context?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          policy.context?.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          cloudProviders
            .find((provider) => provider.id === policy.cloud_cred_id)
            ?.name.toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
      .filter(
        (policy) =>
          filterProvider === "all" || policy.cloud_cred_id === filterProvider
      );
  }, [policies, searchTerm, filterProvider, cloudProviders]);

  useEffect(() => {
    if (policiesData) {
      setPolicies(policiesData);
      if (policiesData.length > 0) {
        setOrganizationWideDefaultRule(
          policiesData.find((p) => !p.cloud_cred_id)?.default_rule || null
        );
      }
    }
  }, [policiesData]);

  const handleOpenOrgDefaultRulePopover = () => {
    setTempOrgDefaultRule(organizationWideDefaultRule);
    setIsOrgDefaultRulePopoverOpen(true);
  };

  const handleSaveOrgDefaultRule = () => {
    if (tempOrgDefaultRule) {
      createOrUpdateOrgWideDefaultRule(orgId, tempOrgDefaultRule);
    } else {
      setIsOrgDefaultRulePopoverOpen(false);
    }
  };

  const handleOrgDefaultRuleChange = (value: string) => {
    setTempOrgDefaultRule(getDefaultRuleEnum(value));
  };

  const handleCreatePolicy = async (newPolicy: CreateCloudPolicyRequest) => {
    const res = await agent.Admin.createCloudPolicy(orgId, newPolicy);
    if (res.success && res.data) {
      setPolicies((prev) => [...prev, res.data]);
    } else {
      setErrorCreatePolicy(res.message || "Failed to create policy");
    }
  };

  const handleUpdatePolicy = (updatedPolicy: CloudPolicy) => {
    const policyIndex = policies.findIndex((p) => p.id === updatedPolicy.id);
    if (policyIndex !== -1) {
      const updatedPolicies = [...policies];
      updatedPolicies[policyIndex] = updatedPolicy;
      setPolicies(updatedPolicies);
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    if (!policyId) return;

    try {
      setIsDeletingPolicy(true);
      const res = await agent.Admin.deleteCloudPolicy(orgId, policyId);
      setIsDeletingPolicy(false);
      if (res.success) {
        // Update local state to remove the policy
        setPolicies((prev) => prev.filter((p) => p.id !== policyId));
      } else {
        notificationContext.showNotification(
          "Failed to delete policy",
          res.message || "Failed to delete policy",
          "error"
        );
      }
    } catch (error) {
      notificationContext.showNotification(
        "Failed to delete policy",
        "An error occurred while deleting the policy",
        "error"
      );
      console.error("Policy deletion error:", error);
    }
  };

  const openEditSheet = (policy: CloudPolicy) => {
    setSelectedPolicy(policy);
    setIsEditSheetOpen(true);
  };

  const handleEditSheetClose = (isOpen: boolean) => {
    setIsEditSheetOpen(isOpen);
    if (!isOpen) {
      // Clear selected policy when drawer closes
      setSelectedPolicy(null);
    }
  };

  const createOrUpdateOrgWideDefaultRule = async (
    orgId: string,
    defaultRule: DefaultRule
  ) => {
    setErrorMessageOrgWideDefaultRule(null);
    const existingOrgWideDefaultRule = policies.find((p) => !p.cloud_cred_id);
    setIsCreatingOrgWideDefaultRule(true);
    console.log("existingOrgWideDefaultRule", existingOrgWideDefaultRule);
    if (!!existingOrgWideDefaultRule) {
      const res = await agent.Admin.updateCloudPolicyDefaultRule(
        orgId,
        existingOrgWideDefaultRule.id || "",
        {
          id: existingOrgWideDefaultRule.id || "",
          default_rule: getDefaultRuleString(defaultRule) as
            | "DEFAULT_RULE_ALLOW"
            | "DEFAULT_RULE_DENY",
        }
      );
      if (res.success && res.data && res.data.cloud_policy) {
        setOrganizationWideDefaultRule(
          res.data.cloud_policy.default_rule || null
        );
        setIsCreatingOrgWideDefaultRule(false);
        setIsOrgDefaultRulePopoverOpen(false);
      } else {
        setErrorMessageOrgWideDefaultRule(
          res.message || "Failed to delete org-wide default rule"
        );
        setIsCreatingOrgWideDefaultRule(false);
      }
      return;
    }
    const request: CreateCloudPolicyRequest = {
      organization_id: orgId,
      context: {
        name: "org-wide-default-rule-auto-generated",
      },
      default_rule: getDefaultRuleString(defaultRule) as
        | "DEFAULT_RULE_ALLOW"
        | "DEFAULT_RULE_DENY",
    };
    const res = await agent.Admin.createCloudPolicy(orgId, request);
    if (res.success && res.data) {
      setOrganizationWideDefaultRule(res.data.default_rule || null);
    } else {
      setErrorMessageOrgWideDefaultRule(
        res.message || "Failed to create org-wide default rule"
      );
      setIsCreatingOrgWideDefaultRule(false);
      return;
    }
    setIsCreatingOrgWideDefaultRule(false);
    setIsOrgDefaultRulePopoverOpen(false);
  };

  return (
    <>
      <div className="mx-auto">
        <FlatCard>
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <div className="flex flex-row gap-2 items-center">
                  <TableCellsIcon className="h-5 w-5 text-primary" />
                  <h1 className="text-xl font-bold text-primary">
                    Cloud Policies
                  </h1>
                </div>
                <p className="text-secondary text-sm">
                  Enable/Disable clouds or manage quotas for an organization's
                  cloud resources.
                </p>
              </div>
              <div className="flex flex-row gap-4">
                <Popover
                  open={isOrgDefaultRulePopoverOpen}
                  onOpenChange={setIsOrgDefaultRulePopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      onClick={handleOpenOrgDefaultRulePopover}
                      variant="secondary"
                    >
                      <GlobeAltIcon className="h-4 w-4 mr-2" />
                      <span className="flex items-center mr-2">
                        Org-Wide Default Rule
                      </span>
                      {organizationWideDefaultRule ===
                      DefaultRule.DEFAULT_RULE_DENY ? (
                        <PulsingIndicator color="red" className="mr-2" />
                      ) : (
                        <PulsingIndicator color="green" className="mr-2" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <h4 className="font-medium leading-none">
                          Organization-Wide Default Rule
                        </h4>
                        <p className="text-xs text-secondary">
                          This rule applies to all cloud resources within the
                          organization if no specific cloud policy matches. It
                          acts as a fallback. Current:{" "}
                          <span className="">
                            {organizationWideDefaultRule ===
                            DefaultRule.DEFAULT_RULE_DENY ? (
                              <span className="text-red-500">Deny</span>
                            ) : (
                              <span className="text-green-500">Allow</span>
                            )}
                          </span>
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <label htmlFor="orgDefaultRuleSelectPopover">
                          Set Default Rule
                        </label>
                        <Select
                          value={
                            tempOrgDefaultRule
                              ? getDefaultRuleString(tempOrgDefaultRule)
                              : "DEFAULT_RULE_ALLOW"
                          }
                          onValueChange={handleOrgDefaultRuleChange}
                        >
                          <SelectTrigger id="orgDefaultRuleSelectPopover">
                            <SelectValue placeholder="Select a default rule" />
                          </SelectTrigger>
                          <SelectContent>
                            {DEFAULT_RULE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setIsOrgDefaultRulePopoverOpen(false)
                            }
                          >
                            Cancel
                          </Button>
                          {isCreatingOrgWideDefaultRule ? (
                            <div className="flex items-center justify-center w-4 h-4">
                              <Spinner type="primary" />
                            </div>
                          ) : (
                            <Button onClick={handleSaveOrgDefaultRule}>
                              Save
                            </Button>
                          )}
                        </div>
                        {errorMessageOrgWideDefaultRule && (
                          <p className="text-red-500 text-sm">
                            {errorMessageOrgWideDefaultRule}
                          </p>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button onClick={() => setIsCreateSheetOpen(true)}>
                  <PlusCircleIcon className="mr-2 h-5 w-5" />
                  Create New Policy
                </Button>
              </div>
            </div>
          </header>

          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full sm:w-auto">
              <Input
                type="search"
                placeholder="Search policies by name, description, or cloud..."
                className="w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 items-center">
              <Select value={filterProvider} onValueChange={setFilterProvider}>
                <SelectTrigger className="w-full flex flex-row w-[200px]">
                  <div className="flex flex-row items-center">
                    <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by cloud" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clouds</SelectItem>
                  {cloudProviders.map((cred) => (
                    <SelectItem key={cred.id} value={cred.id}>
                      {cred.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredPolicies.length > 0 ? (
            <div className={"grid grid-cols-1 gap-6 sm:grid-cols-3 py-4"}>
              {filteredPolicies.map((policy) => (
                <PolicyCard
                  key={policy.id}
                  policy={policy}
                  onManage={() => openEditSheet(policy)}
                  onDelete={() => handleDeletePolicy(policy.id || "")}
                  isDeletingPolicy={isDeletingPolicy}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="mt-2 text-lg font-medium text-slate-900 dark:text-slate-50">
                No policies found
                <br />
                <p className="mt-1 text-sm text-secondary">
                  {searchTerm || filterProvider !== "all"
                    ? "Try adjusting your search or filters."
                    : "Get started by creating a new policy."}
                </p>
              </h3>
              <Button
                variant="primary"
                className="mt-4"
                onClick={() => setIsCreateSheetOpen(true)}
              >
                <PlusCircleIcon className="mr-2 h-4 w-4" /> Create Policy
              </Button>
            </div>
          )}
        </FlatCard>
      </div>

      <EditPolicy
        isOpen={isEditSheetOpen}
        onOpenChange={handleEditSheetClose}
        policy={selectedPolicy || null}
        onUpdatePolicy={handleUpdatePolicy}
        organizationId={orgId}
        cloudProviders={cloudProviders}
      />

      <CreatePolicy
        isOpen={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onCreatePolicy={handleCreatePolicy}
        organizationId={orgId}
        cloudProviders={cloudProviders}
        errorCreatePolicy={errorCreatePolicy}
        setErrorCreatePolicy={setErrorCreatePolicy}
      />
    </>
  );
};
